import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { CustomerServicePopover } from './customer-service-popover';
import style from './index.module.scss';
import TdsLogo from './logo.svg';

enum TdsItemKey {
  Home,
  TencentService,
  BuglyAssistant,
  DocCenter,
  BuglyOverseas,
  CustomerService,
}

enum Env {
  TEST,
  PROD,
}

enum Version {
  WOA,
  PRO,
}

const NAVIGATION_URL: Record<TdsItemKey, Record<Version, Record<Env, string>>> = {
  [TdsItemKey.Home]: {
    [Version.WOA]: {
      [Env.TEST]: 'https://tds-union.testsite.woa.com',
      [Env.PROD]: 'https://tds-union.woa.com',
    },
    [Version.PRO]: {
      [Env.TEST]: 'https://t.tds.qq.com',
      [Env.PROD]: 'https://tds.qq.com',
    },
  },
  [TdsItemKey.TencentService]: {
    [Version.WOA]: {
      [Env.TEST]: 'https://t.tds.qq.com/',
      [Env.PROD]: 'https://tds.qq.com',
    },
    [Version.PRO]: {
      [Env.TEST]: 'https://t.tds.qq.com/',
      [Env.PROD]: 'https://tds.qq.com',
    },
  },
  [TdsItemKey.BuglyAssistant]: {
    [Version.WOA]: {
      [Env.TEST]: 'wxwork://message?uin=8444251803319680',
      [Env.PROD]: 'wxwork://message?uin=8444251803319680',
    },
    [Version.PRO]: {
      [Env.TEST]: 'wxwork://message?uin=8444251803319680',
      [Env.PROD]: 'wxwork://message?uin=8444251803319680',
    },
  },
  [TdsItemKey.DocCenter]: {
    [Version.WOA]: {
      [Env.TEST]: 'https://docs.bugly.woa.com',
      [Env.PROD]: 'https://docs.bugly.woa.com',
    },
    [Version.PRO]: {
      [Env.TEST]: 'https://docs.bugly.woa.com',
      [Env.PROD]: 'https://docs.bugly.woa.com',
    },
  },
  [TdsItemKey.BuglyOverseas]: {
    [Version.WOA]: {
      [Env.TEST]: '#',
      [Env.PROD]: '#',
    },
    [Version.PRO]: {
      [Env.TEST]: 'https://t.bugly.tds.tencent.com/v2/index/main',
      [Env.PROD]: 'https://bugly.tds.tencent.com/v2/index/main',
    },
  },
  [TdsItemKey.CustomerService]: {
    [Version.WOA]: {
      [Env.TEST]: '',
      [Env.PROD]: '',
    },
    [Version.PRO]: {
      [Env.TEST]: '',
      [Env.PROD]: '',
    },
  },
};

interface NavItem {
  title: string;
  key: TdsItemKey;
}

// WOA 版：首页、腾讯端服务、Bugly小助手、文档中心
const WOA_NAVIGATION: NavItem[] = [
  { title: '首页', key: TdsItemKey.Home },
  { title: '腾讯端服务', key: TdsItemKey.TencentService },
  { title: 'Bugly小助手', key: TdsItemKey.BuglyAssistant },
  { title: '文档中心', key: TdsItemKey.DocCenter },
];

// 专业版：腾讯端服务、Bugly海外版、文档中心、客服
const PRO_NAVIGATION: NavItem[] = [
  { title: '腾讯端服务', key: TdsItemKey.TencentService },
  { title: 'Bugly海外版', key: TdsItemKey.BuglyOverseas },
  { title: '文档中心', key: TdsItemKey.DocCenter },
  { title: '客服', key: TdsItemKey.CustomerService },
];

export interface TdsCustomNavigationProps {
  activeMenu?: TdsItemKey;
  isProd: boolean;
  isMobile?: boolean;
  isWoa?: boolean;
}

export { TdsItemKey };

export const TdsCustomNavigation: FC<TdsCustomNavigationProps> = (props) => {
  const {
    isProd,
    isMobile = false,
    isWoa = false,
    activeMenu,
  } = props;

  const [scroll, setScroll] = useState(window.scrollY !== 0);
  const [menuOpen, setMenuOpen] = useState(false);
  const version = isWoa ? Version.WOA : Version.PRO;
  const env = isProd ? Env.PROD : Env.TEST;
  const homeUrl = NAVIGATION_URL[TdsItemKey.Home][version][env];
  const navigationItems = isWoa ? WOA_NAVIGATION : PRO_NAVIGATION;

  const handleOpenUrl = (url: string) => {
    if (!url || url === '#') return;
    if (url.startsWith('wxwork://')) {
      const a = document.createElement('a');
      a.href = url;
      a.click();
      return;
    }
    window.open(url, '_blank');
  };

  useEffect(() => {
    const onScroll = () => setScroll(window.scrollY !== 0);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={classNames(style.tdsNavigation, style.tdsNavigationLight)}
      style={{
        backgroundColor: scroll || menuOpen ? '#fff' : 'transparent',
        boxShadow: scroll || menuOpen ? '0px 1px 4px 0px rgba(42, 58, 134, 0.15)' : 'none',
      }}
    >
      <div className={style.tdsNavigationContent}>
        <img
          src={TdsLogo}
          width={162}
          height={44}
          alt="Bugly 专业版"
          className={style.tdsNavigationLogo}
          onClick={() => window.open(homeUrl, '_self')}
        />
        {!isMobile ? (
          <div className={style.tdsNavigationRow}>
            {navigationItems.map((item) => {
              if (item.key === TdsItemKey.CustomerService) {
                return (
                  <CustomerServicePopover key={item.key}>
                    <a
                      className={classNames(
                        style.tdsNavigationContentItem,
                        item.key === activeMenu ? style.tdsNavigationContentItemActive : '',
                      )}
                    >
                      {item.title}
                    </a>
                  </CustomerServicePopover>
                );
              }
              return (
                <a
                  key={item.key}
                  onClick={() => handleOpenUrl(NAVIGATION_URL[item.key][version][env])}
                  className={classNames(
                    style.tdsNavigationContentItem,
                    item.key === activeMenu ? style.tdsNavigationContentItemActive : '',
                  )}
                >
                  {item.title}
                </a>
              );
            })}
          </div>
        ) : (
          <>
            <div
              className={style.tdsNavigationMenuBtn}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H21M3 12H21M3 18H21" stroke="#1E304A" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            {menuOpen && (
              <div className={style.tdsNavigationDropdownContainer}>
                {navigationItems.map(item => (
                  <a
                    className={style.tdsNavigationDropdownItem}
                    key={item.key}
                    onClick={() => {
                      handleOpenUrl(NAVIGATION_URL[item.key][version][env]);
                      setMenuOpen(false);
                    }}
                  >
                    <div className={style.tdsNavigationDropdownItemTitle}>{item.title}</div>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
